<template>
  <component
    :is="tag"
    class="w-chip"
    :type="tag == 'button' ? defaults.button.type : undefined"
    :disabled="disabled"
    :class="componentClasses"
  >
    <div v-if="tag === 'button' || tag === 'a'" class="state-overlay" />

    <w-icon
      v-if="iconProps.icon && iconPosition === 'left'"
      v-bind="iconProps"
      class="w-chip__icon w-chip__icon--left"
    />

    <span v-if="text !== undefined || $slots.default" class="w-chip__text" :style="textClipStyles">
      <slot>
        <template v-if="typeof text === 'string'">
          {{ translate(text) }}
        </template>

        <template v-else> {{ text }} </template>
      </slot>
    </span>

    <w-icon
      v-if="iconProps.icon && iconPosition === 'right'"
      v-bind="iconProps"
      class="w-chip__icon w-chip__icon--right"
    />

    <w-button
      v-if="clearable && !disabled"
      class="w-chip__clear-button"
      :icon="defaults.icons.list.close"
      :variant="defaults.chip.clearableButton.variant"
      :color="defaults.chip.clearableButton.color"
      :radius="defaults.chip.clearableButton.radius"
      :size="defaults.chip.clearableButton.size"
      @click="$emit('clear')"
    />

    <w-loading v-bind="loadingProps">
      <slot name="loading" />
    </w-loading>
  </component>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';

import { defaults } from '../../composables/defaults';
import { withChipVariantProps, useChipVariantProps } from '../../composables/props/chipVariant';
import { withColorProps, useColorProps } from '../../composables/props/colors';
import { withDisabledProps, useDisabledProps } from '../../composables/props/disabled';
import { withIconProps, useIconProps } from '../../composables/props/icon';
import { withIconPositionProps } from '../../composables/props/iconPosition';
import { withLoadingProps, useLoadingProps } from '../../composables/props/loading';
import { withRadiusProps, useRadiusProps } from '../../composables/props/radius';
import { withSizeProps, useSizeProps } from '../../composables/props/size';
import { withTagProps } from '../../composables/props/tag';
import { useTextAlignProps, withTextAlignProps } from '../../composables/props/textAlign';
import { useTextClipProps, withTextClipProps } from '../../composables/props/textClip';
import { withSchemaOptionalProps } from '../../composables/schema';
import { useTranslation } from '../../composables/translation';
import WButton from '../w-button/WButton.vue';
import WIcon from '../w-icon/WIcon.vue';
import WLoading from '../w-loading/WLoading.vue';

// @vuese
// Przycisk uniwersalny
export default defineComponent({
  components: { WIcon, WLoading, WButton },

  props: {
    ...withTextAlignProps(defaults.chip.textAlign),
    ...withColorProps(defaults.chip.color),
    ...withIconProps(undefined, defaults.chip.iconSize),
    ...withTextClipProps(defaults.chip.textClip),
    ...withTagProps(defaults.chip.tag),
    ...withSizeProps(defaults.chip.size),
    ...withChipVariantProps,
    ...withLoadingProps,
    ...withDisabledProps,
    ...withRadiusProps,
    ...withIconPositionProps,
    ...withSchemaOptionalProps,

    /** Tekst chipsa */
    text: {
      type: [String, Number]
    }
  },

  emits: {
    /** Kliknięcie w przycisk usuwania */
    clear: null
  },

  setup(props) {
    const componentName = 'w-chip';

    const { translate } = useTranslation();

    /** Wyliczone klasy komponentu */
    const variantClasses = useChipVariantProps(componentName, props);
    const colorClasses = useColorProps(componentName, props);
    const radiusClasses = useRadiusProps(componentName, props);
    const disabledClasses = useDisabledProps(componentName, props);
    const sizeClasses = useSizeProps(componentName, props);
    const textAlignClasses = useTextAlignProps(props);

    const componentClasses = computed(() => {
      return [
        variantClasses.value,
        colorClasses.value,
        radiusClasses.value,
        disabledClasses.value,
        textAlignClasses.value,
        sizeClasses.value,

        props.icon ? `${componentName}--has-icon` : undefined,
        props.icon && props.text === undefined ? `${componentName}--only-icon` : undefined
      ];
    });

    return {
      defaults,
      componentClasses,
      loadingProps: useLoadingProps(props),
      iconProps: useIconProps(props),

      textClipStyles: useTextClipProps(props).value,
      translate
    };
  }
});
</script>
<style lang="scss">
@use 'WChip';
</style>
